import { Avatar, Badge, Flex, Tag, Tooltip, Typography } from "antd";
import { Bubble } from "@ant-design/x";
import dayjs from "dayjs";
import {
  ChatChannel,
  ChatThreadMessage,
} from "@rumble-it/common-lib/types/models/ChatConversation.interface";
import { chatMessageStatusConfig } from "../data/chatChannelConfig";
import { ChatMediaAttachment } from "./ChatMediaAttachment";

const { Text } = Typography;

const GROUP_WINDOW_MS = 5 * 60 * 1000;

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "?";

const renderContent = (message: ChatThreadMessage) => (
  <Flex vertical gap={8}>
    {message.body ? <Text>{message.body}</Text> : null}
    {message.media.map((media) => (
      <ChatMediaAttachment key={media.id} media={media} />
    ))}
  </Flex>
);

/**
 * Whether two adjacent messages belong to the same visual group: same sender and
 * status within a short window, so consecutive bubbles collapse (WhatsApp-style).
 */
const sameGroup = (
  a: ChatThreadMessage | undefined,
  b: ChatThreadMessage | undefined,
): boolean => {
  if (!a || !b) {
    return false;
  }

  if (a.direction !== b.direction || a.status !== b.status) {
    return false;
  }

  return (
    Math.abs(dayjs(b.createdAt).diff(dayjs(a.createdAt))) <= GROUP_WINDOW_MS
  );
};

const StatusFooter = ({ message }: { message: ChatThreadMessage }) => {
  const status =
    message.direction === "outbound" && message.status
      ? chatMessageStatusConfig[message.status]
      : undefined;

  return (
    <Flex align="center" gap={4}>
      <Text type="secondary" style={{ fontSize: 11 }}>
        {dayjs(message.createdAt).format("D MMM HH:mm")}
      </Text>
      {status ? (
        <Tooltip title={status.label}>
          <span style={{ fontSize: 12, color: status.color, lineHeight: 1 }}>
            {status.icon}
          </span>
        </Tooltip>
      ) : null}
    </Flex>
  );
};

export const ChatThread = ({
  contactName,
  messages,
}: {
  channel: ChatChannel;
  contactName: string;
  messages: ChatThreadMessage[];
}) => {
  return (
    <Bubble.List
      style={{ height: "100%", paddingInline: 4 }}
      role={{
        outbound: { placement: "end" },
        inbound: { placement: "start" },
      }}
      items={messages.map((message, index) => {
        const outbound = message.direction === "outbound";
        const isGroupStart = !sameGroup(messages[index - 1], message);
        const isGroupEnd = !sameGroup(message, messages[index + 1]);
        const senderName = outbound
          ? (message.sentByName ?? "KCC")
          : contactName;

        return {
          key: message.id,
          role: message.direction,
          content: renderContent(message),
          avatar: isGroupStart ? (
            <Badge dot={message.isNew} offset={[-4, 4]}>
              <Avatar
                size="small"
                style={{
                  backgroundColor: outbound
                    ? "var(--ant-color-primary)"
                    : "#8c8c8c",
                  fontSize: 11,
                }}
              >
                {outbound ? "KCC" : initials(senderName)}
              </Avatar>
            </Badge>
          ) : (
            <div style={{ width: 24 }} />
          ),
          header: isGroupStart ? (
            <Flex align="center" gap={8}>
              <Text strong style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                {senderName}
              </Text>
              {message.isNew ? (
                <Tag color="red" style={{ fontSize: 11, margin: 0 }}>
                  Nieuw
                </Tag>
              ) : null}
            </Flex>
          ) : undefined,
          footer: isGroupEnd ? <StatusFooter message={message} /> : undefined,
        };
      })}
    />
  );
};
